import { Loader2 } from "lucide-react";

import { BrandPlate } from "../../components/BrandMark";
import { Button } from "components/ui/button";

import type { CompletionAction } from "./use-email-link-completion";

/**
 * The whole page while an emailed sign-in link completes, and if it fails.
 *
 * It replaces the sign-in card instead of sitting on top of it: while the link
 * completes there is nothing to click, so nobody navigates away mid sign-in.
 * On failure it offers exactly one next step, the one that can work.
 */
export function EmailLinkCompletionScreen({
  state,
  onRetry,
  onStartOver,
}: {
  state:
    | { phase: "pending" }
    | { phase: "failed"; message: string; action: CompletionAction };
  onRetry: () => void;
  onStartOver: () => void;
}) {
  const pending = state.phase === "pending";
  return (
    <main
      className="flex min-h-screen items-center justify-center bg-gradient-page p-6"
      aria-busy={pending}
      data-testid={pending ? "email-link-completing" : "email-link-failed"}
    >
      <div className="w-full max-w-md rounded-xl border border-border bg-card p-8 text-center text-card-foreground shadow-card dark:border-edge dark:border-lit-edge dark:shadow-lit">
        <BrandPlate className="mx-auto mb-5" />
        {pending ? (
          <>
            <Loader2
              aria-hidden="true"
              className="mx-auto h-8 w-8 animate-spin text-muted-foreground"
            />
            <div role="status" aria-live="polite">
              <h1 className="mt-4 font-display text-2xl font-semibold text-foreground">
                Signing you in…
              </h1>
              <p className="mt-2 text-sm text-muted-foreground">
                This can take a few seconds. Keep this page open.
              </p>
            </div>
          </>
        ) : (
          <>
            <h1 className="font-display text-2xl font-semibold text-foreground">
              We could not sign you in
            </h1>
            <p
              role="alert"
              className="mt-4 rounded-md border border-destructive bg-destructive/10 px-3 py-2 text-left text-sm text-foreground"
            >
              {state.message}
            </p>
            {state.action === "new-link" ? (
              <Button
                variant="cta"
                type="button"
                onClick={onStartOver}
                data-testid="email-link-start-over"
                className="mt-6 w-full"
              >
                Request a new link
              </Button>
            ) : (
              <>
                <Button
                  variant="cta"
                  type="button"
                  onClick={onRetry}
                  data-testid="email-link-retry"
                  className="mt-6 w-full"
                >
                  {state.action === "confirm-address"
                    ? "Enter the address again"
                    : "Try again"}
                </Button>
                <Button
                  type="button"
                  variant="tertiary"
                  onClick={onStartOver}
                  data-testid="email-link-start-over"
                  className="mt-3 w-full"
                >
                  Request a new link instead
                </Button>
              </>
            )}
          </>
        )}
      </div>
    </main>
  );
}
